import { AbsoluteFill, Easing, interpolate, useCurrentFrame } from 'remotion';

// Types
import type { Scene5aProps } from './types';

// Constants
import {
  ANIMATION_DURATIONS,
  CURVED_LINES_ANIMATION,
  CAMERA_CONFIG,
  EXIT_TRANSITION,
  PARALLAX_DEPTHS,
  ELEMENT_DELAYS,
  LAYOUT_POSITIONS,
  getFontSize,
} from './constants';
import { CLAMP_BOTH } from '@/constants/animations';

// Utils
import { getPluralWord } from '@/utils/text';

// Components
import { StatBox } from './components/StatBox';
import { TrainingText } from './components/TrainingText';
import { DiagonalLine } from './components/DiagonalLine';
import { CameraContainer } from './components/CameraContainer';
import { CurvedLinesBackground } from './components/CurvedLinesBackground';

export const Scene5a = ({
  monthsOnTraining = 0,
  trainings = [],
  exitStartFrame,
}: Scene5aProps) => {
  const frame = useCurrentFrame();
  const trainingsCount = trainings.length;

  // Cubic easing for camera movement
  const easedCamera = Easing.inOut(Easing.cubic)(
    interpolate(
      frame,
      [CAMERA_CONFIG.delay, CAMERA_CONFIG.delay + CAMERA_CONFIG.duration],
      [0, 1],
      CLAMP_BOTH,
    ),
  );

  // Parallax effects
  const getParallax = (depth: { start: number; end: number }) =>
    interpolate(easedCamera, [0, 1], [depth.start, depth.end]);

  const parallaxBack = getParallax(PARALLAX_DEPTHS.back);
  const parallaxMid = getParallax(PARALLAX_DEPTHS.mid);
  const parallaxFront = getParallax(PARALLAX_DEPTHS.front);

  // Back easing for bounce effect
  const easedBar = Easing.out(Easing.back(1.70158))(
    interpolate(
      frame,
      [ANIMATION_DURATIONS.bar.start, ANIMATION_DURATIONS.bar.end],
      [0, 1],
      CLAMP_BOTH,
    ),
  );

  const barScaleY = interpolate(
    frame,
    [ANIMATION_DURATIONS.barScaleY.start, ANIMATION_DURATIONS.barScaleY.end],
    [0.01, 1],
    CLAMP_BOTH,
  );

  const isExiting = exitStartFrame !== undefined && frame >= exitStartFrame;
  const exitElapsed = isExiting ? frame - exitStartFrame : 0;

  // StatBox exit progress with exponential easing for rewind effect
  const statBoxExitProgress = isExiting
    ? Easing.in(Easing.exp)(
        interpolate(
          exitElapsed,
          [0, EXIT_TRANSITION.statBoxDuration],
          [0, 1],
          CLAMP_BOTH,
        ),
      )
    : 0;

  // Fade out opacity for text, curved lines, ellipse, and diagonal line
  const fadeOutOpacity = isExiting
    ? interpolate(
        exitElapsed,
        [0, EXIT_TRANSITION.fadeOutDuration],
        [1, 0],
        CLAMP_BOTH,
      )
    : 1;

  // Container opacity for background fade
  const containerOpacity = isExiting
    ? interpolate(
        exitElapsed,
        [EXIT_TRANSITION.bgFadeStartOffset, EXIT_TRANSITION.duration],
        [1, 0],
        CLAMP_BOTH,
      )
    : 1;

  return (
    <AbsoluteFill style={{ opacity: containerOpacity }}>
      {/* 3D Camera Container with perspective and transformations */}
      <CameraContainer cameraProgress={easedCamera}>
        <div className="size-full overflow-hidden border border-black bg-main-gradient">
          {/* Curved Lines Background */}
          <CurvedLinesBackground
            animation={CURVED_LINES_ANIMATION}
            exitOpacity={fadeOutOpacity}
          />

          {/* Background Ellipse */}
          <div
            className="absolute rounded-full bg-primary-800"
            style={{
              ...LAYOUT_POSITIONS.backgroundEllipse,
              transform: `translate(${parallaxBack}px, ${parallaxBack}px)`,
              opacity: fadeOutOpacity,
            }}
          />

          {/* Months Card */}
          <StatBox
            count={monthsOnTraining}
            label={getPluralWord(monthsOnTraining, 'month')}
            animationDelay={ELEMENT_DELAYS.months}
            floatDuration={4}
            floatDirection="up"
            exitProgress={statBoxExitProgress}
            style={{
              ...LAYOUT_POSITIONS.monthsCard,
              fontSize: getFontSize(monthsOnTraining),
              lineHeight: 0.85,
              transform: `translate(${parallaxFront}px, ${parallaxFront}px)`,
            }}
          />

          {/* Training Text */}
          <TrainingText
            barScaleX={easedBar}
            barScaleY={barScaleY}
            parallaxMid={parallaxMid}
            exitOpacity={fadeOutOpacity}
          />

          {/* Topics Card */}
          <StatBox
            count={trainingsCount}
            label={getPluralWord(trainingsCount, 'topic')}
            animationDelay={ELEMENT_DELAYS.topics}
            floatDuration={5}
            floatDirection="down"
            entranceDirection="fromBottom"
            exitProgress={statBoxExitProgress}
            style={{
              ...LAYOUT_POSITIONS.topicsCard,
              fontSize: getFontSize(trainingsCount),
              transform: `translate(${parallaxFront}px, ${parallaxFront}px)`,
            }}
          />

          <DiagonalLine
            animationDelay={ELEMENT_DELAYS.topics}
            exitOpacity={fadeOutOpacity}
          />
        </div>
      </CameraContainer>
    </AbsoluteFill>
  );
};
